import { AddonBase } from "./addon-base";

/**
 * Advanced Addon Register Button Component
 * Manages addon selection state with proper error handling, accessibility, and performance optimizations
 */
export class AddonPdpRegister extends AddonBase {
  static CONFIG = {
    INPUT_NAME: 'properties[add-on]',
    CSS_CLASSES: {
      ADDED: 'addon-register--added',
      ERROR: 'addon-register--error',
      LOADING: 'addon-register--loading'
    },
    TEMPLATES: {
      ADD: 'add',
      REMOVE: 'remove'
    },
    EVENTS: {
      STATE_CHANGE: 'addon:state-change',
      ERROR: 'addon:error'
    }
  };

  constructor() {
    super();

    this._handleClick = this._handleClick.bind(this);
    this._handleKeyDown = this._handleKeyDown.bind(this);
    this._handleStateChange = this._handleStateChange.bind(this);
  }


  /**
   * Get initial state for the component
   * @protected
   * @returns {Object} Initial state object
   */
  _getInitialState() {
    return {
      isAdded: this._getCurrentAddonStatus(),
      isProcessing: false,
      error: null
    };
  }

  static get observedAttributes() {
    return ['addon-id', 'addon-id-physical', 'addon-id-egift', 'product-form-id', 'disabled'];
  }

  /**
   * Get allowed state keys for default-state parsing
   * @protected
   * @returns {string[]} Array of allowed state keys
   */
  _getAllowedStateKeys() {
    return ['isAdded', 'isProcessing', 'error'];
  }

  /**
   * Validate required attributes
   * @protected
   */
  _validateAttributes() {
    const addonId = this.getAttribute('addon-id');
    const addonIdPhysical = this.getAttribute('addon-id-physical');
    const addonIdEgift = this.getAttribute('addon-id-egift');
    const productFormId = this.getAttribute('product-form-id');

    const missingAttributes = [];

    if (!addonIdPhysical) {
      missingAttributes.push('addon-id-physical');
    }

    if (!addonIdEgift) {
      missingAttributes.push('addon-id-egift');
    }

    if (!productFormId) {
      missingAttributes.push('product-form-id');
    }

    if (!addonId) {
      missingAttributes.push('addon-id');
    }

    if (missingAttributes.length > 0) {
      throw new Error(`[AddonPdpRegister] Missing required attributes: ${missingAttributes.join(', ')}`);
    }

    this._addonId = addonId;
    this._addonIdPhysical = addonIdPhysical;
    this._addonIdEgift = addonIdEgift;
    this._addonVariantIds = `${addonIdPhysical}::${addonIdEgift}`;
    this._productFormId = productFormId;
  }

  /**
   * Cache frequently accessed DOM elements
   * @protected
   */
  _cacheDOMReferences() {
    const button = this.querySelector('button');
    if (!button) {
      throw new Error('AddonPdpRegister: button element not found');
    }

    this._domRefs.set('button', button);
    this._domRefs.set('addTemplate', this.querySelector('template[name="add"]'));
    this._domRefs.set('removeTemplate', this.querySelector('template[name="remove"]'));

    // Cache form reference (may not exist initially)
    this._updateFormReference();
  }

  /**
   * Update cached form reference
   * @protected
   */
  _updateFormReference() {
    const form = document.getElementById(this._productFormId);
    this._domRefs.set('form', form);
  }

  /**
   * Setup event listeners with proper cleanup tracking
   * @protected
   */
  _setupEventListeners() {
    const button = this._domRefs.get('button');

    this._addEventListener(button, 'click', this._handleClick);
    this._addEventListener(button, 'keydown', this._handleKeyDown);
    this._addEventListener(document, AddonPdpRegister.CONFIG.EVENTS.STATE_CHANGE, this._handleStateChange);
  }

  /**
   * Setup accessibility attributes
   * @protected
   */
  _setupAccessibility() {
    const button = this._domRefs.get('button');

    button.setAttribute('aria-pressed', this._state.isAdded.toString());

    if (!button.hasAttribute('tabindex')) {
      button.setAttribute('tabindex', '0');
    }
  }


  /**
   * Handle click events
   * @protected
   */
  async _handleClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this._state.isProcessing || this.hasAttribute('disabled')) {
      return;
    }

    try {
      await this._toggleAddonState();
    } catch (error) {
      this._handleError('Failed to toggle addon state', error);
    }
  }

  /**
   * Handle keyboard events for accessibility
   * @protected
   */
  _handleKeyDown(event) {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this._handleClick(event);
    }
  }

  /**
   * Handle state changes from other instances
   * @protected
   */
  _handleStateChange(event) {
    if (event.detail.addonId === this._addonId && event.detail.source !== this) {
      this._updateState({ isAdded: event.detail.isAdded });
    }
  }

  /**
   * Handle attribute changes
   * @protected
   */
  _handleAttributeChange(name, newValue) {
    switch (name) {
      case 'disabled':
        this._updateDisabledState(newValue !== null);
        break;
      case 'addon-id':
        this._addonId = newValue;
        this._initializeState();
        this._render();
        break;
      case 'addon-id-physical':
        this._addonIdPhysical = newValue;
        this._initializeState();
        this._render();
        break;
      case 'addon-id-egift':
        this._addonIdEgift = newValue;
        this._initializeState();
        this._render();
        break;
      case 'product-form-id':
        this._productFormId = newValue;
        this._updateFormReference();
        break;
    }
  }

  /**
   * Toggle addon state with proper state management
   * @protected
   */
  async _toggleAddonState() {
    const newState = !this._state.isAdded;

    if (newState && this.hasAttribute('disabled')) {
      return;
    }

    this._updateState({ isProcessing: true, error: null });

    try {
      if (newState) {
        await this._registerAddon();
      } else {
        await this._unregisterAddon();
      }

      this._updateState({
        isAdded: newState,
        isProcessing: false
      });

      this._broadcastStateChange();

      this._requestModalClose();

    } catch (error) {
      this._updateState({ isProcessing: false });
      throw error;
    }
  }

  /**
   * Register addon with improved error handling
   * @protected
   */
  async _registerAddon() {
    const form = this._domRefs.get('form');
    if (!form) {
      throw new Error('Product form not found');
    }

    const inputElement = form.querySelector(`input[name="${AddonPdpRegister.CONFIG.INPUT_NAME}"]`);

    if (inputElement) {
      const currentValues = this._parseAddonValues(inputElement.value);
      if (!currentValues.includes(this._addonVariantIds)) {
        currentValues.push(this._addonVariantIds);
        inputElement.value = currentValues.join(',');
      }
    } else {
      const newInput = this._createAddonInput(this._addonVariantIds);
      form.appendChild(newInput);
    }
  }

  /**
   * Unregister addon with improved error handling
   * @protected
   */
  async _unregisterAddon() {
    const inputElement = document.querySelector(`input[name="${AddonPdpRegister.CONFIG.INPUT_NAME}"]`);

    if (!inputElement) {
      return;
    }

    const currentValues = this._parseAddonValues(inputElement.value);
    const updatedValues = currentValues.filter(id => id !== this._addonVariantIds);

    if (updatedValues.length === 0) {
      inputElement.remove();
    } else {
      inputElement.value = updatedValues.join(',');
    }
  }

  /**
   * Get current addon status from DOM
   * @private
   */
  _getCurrentAddonStatus() {
    const inputElement = document.querySelector(`input[name="${AddonPdpRegister.CONFIG.INPUT_NAME}"]`);
    if (!inputElement) return false;

    const values = this._parseAddonValues(inputElement.value);
    return values.includes(this._addonVariantIds);
  }

  /**
   * Parse addon values with validation
   * @private
   */
  _parseAddonValues(value) {
    return value ? value.split(',').map(v => v.trim()).filter(Boolean) : [];
  }

  /**
   * Create addon input element
   * @private
   */
  _createAddonInput(value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = AddonPdpRegister.CONFIG.INPUT_NAME;
    input.value = value;
    return input;
  }

  /**
   * Render component based on current state
   * @private
   */
  _render() {
    const button = this._domRefs.get('button');
    if (!button) return;

    this._updateButtonClasses();
    this._updateButtonContent();
    this._updateAccessibility();
    this._updateDisabledState();
    this._updateDataAttributes();
  }

  /**
   * Update button CSS classes
   * @private
   */
  _updateButtonClasses() {
    const button = this._domRefs.get('button');
    const { CSS_CLASSES } = AddonPdpRegister.CONFIG;

    button.classList.toggle(CSS_CLASSES.ADDED, this._state.isAdded);
    button.classList.toggle(CSS_CLASSES.LOADING, this._state.isProcessing);
    button.classList.toggle(CSS_CLASSES.ERROR, !!this._state.error);
  }

  /**
   * Update button content from templates
   * @private
   */
  _updateButtonContent() {
    const button = this._domRefs.get('button');
    const templateName = this._state.isAdded ? 'removeTemplate' : 'addTemplate';
    const template = this._domRefs.get(templateName);

    if (template && template.content) {
      const fragment = template.content.cloneNode(true);
      button.replaceChildren(fragment);
    }
  }

  /**
   * Update accessibility attributes
   * @private
   */
  _updateAccessibility() {
    const button = this._domRefs.get('button');
    button.setAttribute('aria-pressed', this._state.isAdded.toString());

    if (this._state.error) {
      button.setAttribute('aria-describedby', `addon-${this._addonId}-error`);
    }
  }

  /**
   * Update disabled state
   * @private
   */
  _updateDisabledState(forceDisabled = false) {
    const button = this._domRefs.get('button');
    const shouldDisable = forceDisabled || this._state.isProcessing || this.hasAttribute('disabled');

    button.disabled = shouldDisable;
    button.setAttribute('aria-disabled', shouldDisable.toString());
  }

  /**
   * Update data attributes based on current state
   * @private
   */
  _updateDataAttributes() {
    const state = this._state.isAdded ? 'added' : 'removed';
    this.setAttribute('data-state', state);
  }

  /**
   * Broadcast state change to other instances
   * @private
   */
  _broadcastStateChange() {
    const event = new CustomEvent(AddonPdpRegister.CONFIG.EVENTS.STATE_CHANGE, {
      detail: {
        addonId: this._addonId,
        isAdded: this._state.isAdded,
        source: this
      },
      bubbles: true
    });

    document.dispatchEvent(event);
  }

  /**
   * Request modal closure with success feedback delay
   * @private
   */
  _requestModalClose() {
    // Check if this component is inside a modal
    const modal = this.closest('.addon-modal');

    if (modal) {
      // Find the addon-details component that contains this modal
      const addonDetailsElement = modal.closest('addon-details');

      if (addonDetailsElement && addonDetailsElement.isOpen) {
        // Close the modal with a delay to show success feedback
        setTimeout(() => {
          addonDetailsElement.closeModal();
        }, 500);
      }
    }
  }

  /**
   * Get current addon state
   */
  get isAdded() {
    return this._state?.isAdded || false;
  }

  /**
   * Get processing state
   */
  get isProcessing() {
    return this._state?.isProcessing || false;
  }

  /**
   * Programmatically add addon
   */
  async addAddon() {
    if (!this._state.isAdded) {
      await this._toggleAddonState();
    }
  }

  /**
   * Programmatically remove addon
   */
  async removeAddon() {
    if (this._state.isAdded) {
      await this._toggleAddonState();
    }
  }

  /**
   * Refresh component state from DOM
   */
  refresh() {
    this._initializeState();
    this._render();
  }
}